import React from "react";
import { MenuItem } from "@mui/material";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import DeleteOutlinedIcon from "@mui/icons-material/DeleteOutlined";
import PictureAsPdfOutlinedIcon from "@mui/icons-material/PictureAsPdfOutlined";
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import BuildOutlinedIcon from '@mui/icons-material/BuildOutlined';
import ShareIcon from "../../../shared/assets/icons/ShareIcon";
import PaperWithCrossIcon from "../../../shared/assets/icons/PaperWithCrossIcon";
import PaperWithRightCornerBentIcon from "../../../shared/assets/icons/PaperWithRightCornerBentIcon";
import TableActionButton from "../../../shared/components/tables/TableActionButton";
import { ServiceReport, ServiceReportStatusCode } from "../models/ServiceReportsModel";
import useTranslation from "../../../shared/hooks/useTranslation";
import usePermission from "../../../shared/hooks/usePermission";
import { Modules } from "../../../core/models/PermissionModels";
import PermissionGuard from "../../../shared/components/PermissionGuard";
import { PermissionLevel } from "../../../core/models/PermissionModels";

const menuItemStyles = {
  fontFamily: 'var(--font-sans)',
  fontSize: "14px",
  fontStyle: "normal",
  fontWeight: "500",
  lineHeight: "22px"
};

interface ServiceReportActionsMenuProps {
  serviceReport: ServiceReport;
  currentUserId?: string;
  onShare: (item: ServiceReport) => void;
  onDownloadPdf: (item: ServiceReport) => void;
  onEdit: (item: ServiceReport) => void;
  onMarkAsSolved: (item: ServiceReport) => void;
  onEditSolvedReport: (item: ServiceReport) => void;
  onShowDetails: (item: ServiceReport) => void;
  onDelete: (item: ServiceReport) => void;
  onCreateServiceRequest?: (item: ServiceReport) => void;
}

const ServiceReportActionsMenu = ({
  serviceReport,
  currentUserId,
  onShare,
  onDownloadPdf,
  onEdit,
  onMarkAsSolved,
  onEditSolvedReport,
  onShowDetails,
  onDelete,
  onCreateServiceRequest
}: ServiceReportActionsMenuProps) => {
  const { translate } = useTranslation(["myMachinery", "common"]);
  const { canWrite } = usePermission(Modules.Machinery);

  // Workers can only edit their own service reports
  const isOwnReport = !!currentUserId && serviceReport.createdByUserId === currentUserId;
  const canEdit = canWrite || isOwnReport;

  if (serviceReport.statusCode === ServiceReportStatusCode.inProgress) {
    return (
      <TableActionButton stopPropagation={true}>
        <MenuItem sx={menuItemStyles} onClick={() => onShowDetails(serviceReport)}>
          <VisibilityOutlinedIcon />
          {translate("showDetails")}
        </MenuItem>

        <MenuItem sx={menuItemStyles} onClick={() => onShare(serviceReport)}>
          <ShareIcon />
          {translate("share")}
        </MenuItem>

        <MenuItem sx={menuItemStyles} onClick={() => onDownloadPdf(serviceReport)}>
          <PictureAsPdfOutlinedIcon />
          {translate("downloadPdf")}
        </MenuItem>

        {canWrite && (
          <MenuItem sx={menuItemStyles} onClick={() => onCreateServiceRequest?.(serviceReport)}>
            <BuildOutlinedIcon />
            {translate("createServiceRequest")}
          </MenuItem>
        )}

        {canEdit && (
          <MenuItem sx={menuItemStyles} onClick={() => onEdit(serviceReport)}>
            <EditOutlinedIcon />
            {translate("common:buttons.edit")}
          </MenuItem>
        )}

        {canWrite && (
          <MenuItem sx={menuItemStyles} onClick={() => onMarkAsSolved(serviceReport)}>
            <PaperWithCrossIcon />
            {translate("markAsSolved")}
          </MenuItem>
        )}

        <PermissionGuard module={Modules.Machinery} level={PermissionLevel.Admin}>
          <MenuItem sx={menuItemStyles} onClick={() => onDelete(serviceReport)}>
            <DeleteOutlinedIcon />
            {translate("common:buttons.delete")}
          </MenuItem>
        </PermissionGuard>
      </TableActionButton>
    );
  }

  if (serviceReport.statusCode === ServiceReportStatusCode.solved) {
    return (
      <TableActionButton>
        <MenuItem sx={menuItemStyles} onClick={() => onShowDetails(serviceReport)}>
          <VisibilityOutlinedIcon />
          {translate("showDetails")}
        </MenuItem>

        <MenuItem sx={menuItemStyles} onClick={() => onShare(serviceReport)}>
          <ShareIcon />
          {translate("share")}
        </MenuItem>

        <MenuItem sx={menuItemStyles} onClick={() => onDownloadPdf(serviceReport)}>
          <PictureAsPdfOutlinedIcon />
          {translate("downloadPdf")}
        </MenuItem>

        {canWrite && (
          <MenuItem sx={menuItemStyles} onClick={() => onCreateServiceRequest?.(serviceReport)}>
            <BuildOutlinedIcon />
            {translate("createServiceRequest")}
          </MenuItem>
        )}

        {canEdit && (
          <MenuItem sx={menuItemStyles} onClick={() => onEditSolvedReport(serviceReport)}>
            <PaperWithRightCornerBentIcon />
            {translate("editSolvedReport")}
          </MenuItem>
        )}

        <PermissionGuard module={Modules.Machinery} level={PermissionLevel.Admin}>
          <MenuItem sx={menuItemStyles} onClick={() => onDelete(serviceReport)}>
            <DeleteOutlinedIcon />
            {translate("common:buttons.delete")}
          </MenuItem>
        </PermissionGuard>
      </TableActionButton>
    );
  }

  return null;
};

export default ServiceReportActionsMenu;
